export interface GlobalSearchResult {
    value?: string;
    text?: string;
    form_number?: string | number;
    order_type?: string;
    purchase_order_id?: string | number;
    po_name?: string;
    bill_id?: string | number;
    bill_reference_number?: string | number;
    job_number?: string | number;
    job_name?: string;
    shop_number?: string | number;
    shop_name?: string;
    shop_type?: string;
    client_id?: string | number;
    client_name?: string;
    date_created?: string;
}

export interface GlobalSearchSection {
    category: string;
    data: GlobalSearchResult[];
}

export interface GlobalSearchResponse {
    results?: GlobalSearchSection[];
}

export interface GlobalSearchResultLabel {
    primary: string;
    secondary?: string;
}

const SHOP_TYPE_LABEL: Record<string, string> = {
    connected: "Shopify Connected Shop",
    popup: "Pop-up Shop",
    company: "Company Shop",
    marketing: "Marketing Shop",
};

const getShopType = (result: GlobalSearchResult): string => (
    SHOP_TYPE_LABEL[result.shop_type ?? ""] ?? "Shop"
);

export const getGlobalSearchResultHref = (result: GlobalSearchResult): string => {
    if (result.form_number && result.job_number) {
        if (result.purchase_order_id) {
            return `/project/${result.job_number}/production`;
        }

        const orderType = result.order_type ?? "";
        const orderTypePath = "SALES ORDER" === orderType
            ? "sales-order"
            : orderType.toLowerCase();
        const orderPath = "OPPORTUNITY" === orderType
            ? ""
            : `/${orderTypePath}/${result.form_number}`;

        return `/project/${result.job_number}${orderPath}`;
    }

    if (result.bill_id) {
        return `/project/${result.job_number}/bills/${result.bill_id}`;
    }

    if (result.job_number && result.job_name) {
        return `/project/${result.job_number}`;
    }

    if (result.shop_number) {
        return `/shop/${result.shop_number}`;
    }

    if (result.client_id) {
        return `/client.php?id=${result.client_id}`;
    }

    return `/global_search.php${result.value ? `?str=${encodeURIComponent(result.value)}` : ""}`;
};

const formatOrderType = (orderType: string): string => {
    const normalizedOrderType = orderType.toLowerCase();

    return normalizedOrderType.charAt(0).toUpperCase() + normalizedOrderType.slice(1);
};

const createResultLabel = (
    ...parts: Array<string | number | undefined>
): GlobalSearchResultLabel => {
    const [primary = "Search result", ...secondaryParts] = parts
        .filter((part): part is string | number => Boolean(part))
        .map(String);

    return {
        primary,
        secondary: 0 < secondaryParts.length
            ? secondaryParts.join(" - ")
            : undefined,
    };
};

export const getGlobalSearchResultLabel = (
    result: GlobalSearchResult,
): GlobalSearchResultLabel => {
    if (result.form_number && result.job_number && !result.purchase_order_id) {
        return createResultLabel(
            result.client_name,
            `Project #${result.job_number}`,
            result.job_name,
            `${formatOrderType(result.order_type ?? "Order")} #${result.form_number}`,
        );
    }

    if (result.bill_id) {
        return createResultLabel(
            result.client_name,
            `Project #${result.job_number}`,
            result.job_name,
            `Bill #${result.bill_reference_number}`,
        );
    }

    if (result.job_number && result.job_name) {
        return createResultLabel(
            result.client_name,
            `Project #${result.job_number}`,
            result.job_name,
        );
    }

    if (result.po_name || result.purchase_order_id) {
        return createResultLabel(result.po_name ?? `PO #${result.form_number}`);
    }

    if (result.shop_number) {
        return createResultLabel(
            result.client_name,
            `${getShopType(result)} #${result.shop_number}`,
            result.shop_name,
        );
    }

    if (result.client_id) {
        return createResultLabel(result.client_name ?? "Client");
    }

    return createResultLabel(result.text ?? result.value ?? "Search result");
};
